<script setup lang="ts">
import { BookOpenText, FileText, Trash2, UploadCloud } from 'lucide-vue-next'
import { httpsCallable } from 'firebase/functions'
import { ref as storageRef, uploadBytesResumable } from 'firebase/storage'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { firebaseClient } from '@/firebase'
import { useAuthStore } from '@/stores/auth'
import { useKnowledgeStore, useProjectStore } from '@/stores/domain'

const auth = useAuthStore()
const projectsStore = useProjectStore()
const knowledgeStore = useKnowledgeStore()
const { projects } = storeToRefs(projectsStore)
const { documents } = storeToRefs(knowledgeStore)
const projectId = ref('')
const uploading = ref(false)
const progress = ref(0)
const error = ref('')
const visibleDocuments = computed(() => documents.value.filter((item) => !projectId.value || item.projectId === projectId.value))

const sha256 = async (file: File) => Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', await file.arrayBuffer())))
  .map((byte) => byte.toString(16).padStart(2, '0')).join('')

const upload = async (event: Event) => {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  const uid = auth.user?.uid
  if (!file || !uid || !projectId.value) return
  uploading.value = true; progress.value = 0; error.value = ''
  try {
    const digest = await sha256(file)
    const safeName = `${Date.now()}-${file.name.replace(/[^A-Za-z0-9._-]/g, '_')}`
    const task = uploadBytesResumable(
      storageRef(firebaseClient.storage, `accounts/${uid}/projects/${projectId.value}/source/${safeName}`), file,
      { contentType: file.type || 'text/plain', customMetadata: { accountId: uid, projectId: projectId.value,
        uploadedBy: uid, originalName: file.name, sha256: digest, ragStatus: 'queued', retentionCategory: 'project_source' } },
    )
    await new Promise<void>((resolve, reject) => task.on('state_changed',
      (snapshot) => { progress.value = Math.round(snapshot.bytesTransferred / snapshot.totalBytes * 100) }, reject, resolve))
  } catch (value) { error.value = value instanceof Error ? value.message : 'Upload failed.' }
  finally { uploading.value = false; input.value = '' }
}

const remove = async (documentId: string) => {
  error.value = ''
  try { await httpsCallable(firebaseClient.functions, 'deleteKnowledgeDocument')({ documentId }) }
  catch (value) { error.value = value instanceof Error ? value.message : 'Deletion failed.' }
}
</script>

<template>
  <div class="section-page">
    <header class="section-page__header"><div><span class="eyebrow">Retrieval</span><h1>Knowledge</h1><p>Upload scoped project sources. SkipHR indexes them for cited semantic search without copying raw material into Manager memory.</p></div></header>
    <p v-if="error" class="connection-error" role="alert">{{ error }}</p>
    <section class="detail-card detail-card--wide">
      <div class="detail-card__head"><div><span class="eyebrow">Source ingestion</span><h2>Project files</h2></div><BookOpenText :size="18" /></div>
      <div class="knowledge-upload">
        <label>Project<select v-model="projectId"><option value="" disabled>Select a project</option><option v-for="project in projects" :key="project.id" :value="project.id">{{ project.name }}</option></select></label>
        <label class="button button--primary" :class="{ 'is-disabled': !projectId || uploading }"><UploadCloud :size="16" /> {{ uploading ? `Uploading ${progress}%` : 'Upload source' }}<input type="file" accept="text/*,.json,.csv,.xml" :disabled="!projectId || uploading" @change="upload" /></label>
      </div>
      <div v-if="visibleDocuments.length" class="team-list knowledge-list">
        <div v-for="document in visibleDocuments" :key="document.id"><i class="specialist-avatar specialist-avatar--blue"><FileText :size="15" /></i><span><strong>{{ document.title }}</strong><small>{{ document.status }}<template v-if="document.chunkCount"> · {{ document.chunkCount }} chunks</template></small></span><button v-if="document.status !== 'deleted'" class="icon-button" type="button" aria-label="Delete document" @click="remove(document.id)"><Trash2 :size="15" /></button></div>
      </div>
      <p v-else>No indexed sources for this project.</p>
    </section>
  </div>
</template>
